/**
 * procurementInvoiceApi — RTK Query slice cho /purchase/invoices/* (Procurement Invoice).
 * Tag types: 'ProcInvoice' (list) + 'ProcInvoiceDetail' (single).
 */
import { baseApi } from './baseApi'
import type {
  DataResponse,
  InvoiceContractSnapshot,
  InvoiceDeclarationDateWrite,
  InvoiceDetail,
  InvoiceListItem,
  InvoiceListQuery,
  InvoiceLookupResponse,
  InvoiceSuggestHoadonItem,
  InvoiceSuggestHoadonSaveResult,
  InvoiceSuggestHoadonSaveRow,
  InvoiceWrite,
  MessageResponse,
  PageResponse,
} from '@/types/api'

export const procurementInvoiceApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listInvoices: b.query<PageResponse<InvoiceListItem>, InvoiceListQuery | void>({
      query: (params) => ({ url: '/purchase/invoices/list', params: params ?? {} }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({ type: 'ProcInvoice' as const, id: it.invoice_id })),
              { type: 'ProcInvoice' as const, id: 'LIST' },
            ]
          : [{ type: 'ProcInvoice' as const, id: 'LIST' }],
    }),
    getInvoice: b.query<DataResponse<InvoiceDetail>, number>({
      query: (id) => `/purchase/invoices/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'ProcInvoiceDetail', id }],
    }),
    createInvoice: b.mutation<DataResponse<InvoiceDetail>, InvoiceWrite>({
      query: (body) => ({ url: '/purchase/invoices/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProcInvoice', id: 'LIST' }],
    }),
    updateInvoice: b.mutation<DataResponse<InvoiceDetail>, { id: number; body: InvoiceWrite }>({
      query: ({ id, body }) => ({ url: `/purchase/invoices/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'ProcInvoice', id: arg.id },
        { type: 'ProcInvoice', id: 'LIST' },
        { type: 'ProcInvoiceDetail', id: arg.id },
      ],
    }),
    deleteInvoice: b.mutation<MessageResponse, number>({
      query: (id) => ({ url: `/purchase/invoices/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'ProcInvoice', id },
        { type: 'ProcInvoice', id: 'LIST' },
      ],
    }),
    /** Bulk set declaration_date + receive_geo_date cho các invoice ticked. */
    setInvoiceDeclarationDate: b.mutation<MessageResponse, InvoiceDeclarationDateWrite>({
      query: (body) => ({ url: '/purchase/invoices/declaration-date', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProcInvoice', id: 'LIST' }],
    }),
    // Accounting - Input Invoices (suggest hoadon)
    listInvoiceSuggestHoadon: b.query<PageResponse<InvoiceSuggestHoadonItem>, void>({
      query: () => '/purchase/invoices/suggest-hoadon/list',
      keepUnusedDataFor: 0,
    }),
    saveInvoiceSuggestHoadon: b.mutation<
      DataResponse<InvoiceSuggestHoadonSaveResult>,
      { rows: InvoiceSuggestHoadonSaveRow[] }
    >({
      query: (body) => ({ url: '/purchase/invoices/suggest-hoadon/save', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProcInvoice', id: 'LIST' }],
    }),
    // Contract snapshot (DLookup bundle cho dialog Add)
    invoiceContractSnapshot: b.query<
      DataResponse<InvoiceContractSnapshot>,
      { contractNo: string }
    >({
      query: ({ contractNo }) =>
        `/purchase/invoices/contract-snapshot?contractNo=${encodeURIComponent(contractNo)}`,
      keepUnusedDataFor: 0,
    }),
    // Lookups
    invoicePContractsLookup: b.query<InvoiceLookupResponse, { q?: string } | void>({
      query: (arg) => ({
        url: '/purchase/invoices/lookups/p-contracts',
        params: arg && arg.q ? { q: arg.q } : {},
      }),
    }),
    invoiceConsignmentContractsLookup: b.query<InvoiceLookupResponse, { q?: string } | void>({
      query: (arg) => ({
        url: '/purchase/invoices/lookups/consignment-contracts',
        params: arg && arg.q ? { q: arg.q } : {},
      }),
    }),
    invoiceAllContractsLookup: b.query<InvoiceLookupResponse, { q?: string } | void>({
      query: (arg) => ({
        url: '/purchase/invoices/lookups/all-contracts',
        params: arg && arg.q ? { q: arg.q } : {},
      }),
    }),
    invoiceWarehousesLookup: b.query<InvoiceLookupResponse, void>({
      query: () => '/purchase/invoices/lookups/warehouses',
    }),
    invoicePriceUnitsLookup: b.query<InvoiceLookupResponse, void>({
      query: () => '/purchase/invoices/lookups/price-units',
    }),
  }),
})

export const {
  useListInvoicesQuery,
  useGetInvoiceQuery,
  useLazyGetInvoiceQuery,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
  useDeleteInvoiceMutation,
  useSetInvoiceDeclarationDateMutation,
  useListInvoiceSuggestHoadonQuery,
  useSaveInvoiceSuggestHoadonMutation,
  useLazyInvoiceContractSnapshotQuery,
  useInvoicePContractsLookupQuery,
  useLazyInvoicePContractsLookupQuery,
  useInvoiceConsignmentContractsLookupQuery,
  useLazyInvoiceConsignmentContractsLookupQuery,
  useInvoiceAllContractsLookupQuery,
  useLazyInvoiceAllContractsLookupQuery,
  useInvoiceWarehousesLookupQuery,
  useInvoicePriceUnitsLookupQuery,
} = procurementInvoiceApi
